"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var tabbable = require("tabbable");
var DomUtil_1 = require("../../utils/DomUtil");
var Overlay_1 = require("../../utils/Overlay");
var PointerEvents_1 = require("../../utils/PointerEvents");
var TooltipConsts_1 = require("./TooltipConsts");
exports.TooltipPosition = TooltipConsts_1.TooltipPosition;
/**
 * Wrapper for child element(s) which displays a popup when the child element(s) receive focus or are hovered over.
 */
var Tooltip = /** @class */ (function (_super) {
    tslib_1.__extends(Tooltip, _super);
    function Tooltip() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.id = DomUtil_1.generateId();
        _this.waitingOnGetContent = false;
        _this.state = {};
        _this.handleWrappedChildrenRef = function (element) {
            _this.wrappedChildrenElement = element;
        };
        _this.handleTooltipOverlayContentRef = function (element) {
            _this.tooltipContentElement = element ? element.firstElementChild : null;
        };
        _this.handleClick = function (e) {
            _this.state.content && _this.state.anchorClientRect ? _this.dismissTooltipOverlay() : _this.showTooltipOverlay(e);
        };
        _this.handleKeyUp = function (e) {
            if (_this.state.content && _this.state.anchorClientRect && e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                _this.dismissTooltipOverlay();
            }
        };
        _this.handleMouseMove = function (e) {
            if (_this.state.content && _this.state.anchorClientRect && !_this.props.followCursor) {
                return;
            }
            _this.showTooltipOverlay(e);
        };
        _this.showTooltipOverlay = function (e) {
            _this.setState({ anchorClientRect: _this.getAnchorClientRectFromEvent(e) });
            if (_this.state.content || _this.waitingOnGetContent) {
                return;
            }
            _this.waitingOnGetContent = true;
            var setContent = function (content) {
                if (_this.waitingOnGetContent) {
                    Tooltip.dismissDisplayedTooltip();
                    Tooltip.displayedTooltip = { dismiss: _this.dismissTooltipOverlay, id: _this.id };
                    _this.setState({ content: content }, function () { _this.waitingOnGetContent = false; });
                }
            };
            var getContent = _this.props.getContent();
            if (Promise.resolve(getContent) === getContent) {
                getContent
                    .then(setContent)
                    .catch(function (error) {
                    console.warn({ message: "'getContent' promise rejected", error: error });
                    _this.setState({ anchorClientRect: undefined }, function () { _this.waitingOnGetContent = false; });
                });
            }
            else {
                setContent(getContent);
            }
        };
        _this.dismissTooltipOverlay = function () {
            if (Tooltip.displayedTooltip && _this.id === Tooltip.displayedTooltip.id) {
                Tooltip.displayedTooltip = undefined;
            }
            _this.waitingOnGetContent = false;
            _this.setState({ anchorClientRect: undefined, content: undefined, width: undefined });
        };
        _this.getAnchorClientRect = function () {
            return _this.state.anchorClientRect;
        };
        return _this;
    }
    Tooltip.dismissDisplayedTooltip = function () {
        if (Tooltip.displayedTooltip) {
            var displayedTooltip = tslib_1.__assign({}, Tooltip.displayedTooltip);
            Tooltip.displayedTooltip = undefined;
            try {
                displayedTooltip.dismiss();
            }
            catch (error) {
                console.warn({ message: 'failed to dismiss displayed tooltip', error: error });
            }
        }
    };
    Tooltip.prototype.componentDidMount = function () {
        this.maybeUpdateStateForRenderedChildrenAndTooltip();
    };
    Tooltip.prototype.componentDidUpdate = function () {
        this.maybeUpdateStateForRenderedChildrenAndTooltip();
    };
    Tooltip.prototype.componentWillUnmount = function () {
        this.dismissTooltipOverlay();
    };
    Tooltip.prototype.maybeUpdateStateForRenderedChildrenAndTooltip = function () {
        var stateUpdate = {};
        var containsTabbableChildren = !!this.wrappedChildrenElement && tabbable(this.wrappedChildrenElement).length > 0;
        if (containsTabbableChildren !== this.state.containsTabbableChildren) {
            stateUpdate.containsTabbableChildren = containsTabbableChildren;
        }
        if (this.state.content && this.state.anchorClientRect && this.tooltipContentElement && this.state.width === undefined) {
            stateUpdate.width = this.tooltipContentElement.getBoundingClientRect().width + TooltipConsts_1.AggregateTooltipPadding;
        }
        if (Object.keys(stateUpdate).length > 0) {
            this.setState(stateUpdate);
        }
    };
    Tooltip.prototype.getAnchorClientRectFromEvent = function (e) {
        var isFocusEvent = e.type === 'focus';
        if (isFocusEvent && this.state.anchorClientRect) {
            return this.state.anchorClientRect;
        }
        else if (!this.wrappedChildrenElement) {
            return undefined;
        }
        else if (isFocusEvent || !this.props.followCursor) {
            return this.adjustClientRectForOffset(this.wrappedChildrenElement.getBoundingClientRect());
        }
        else {
            e = e;
            return this.adjustClientRectForOffset({
                bottom: e.clientY,
                height: 0,
                left: e.clientX,
                right: e.clientX,
                top: e.clientY,
                width: 0,
            });
        }
    };
    Tooltip.prototype.adjustClientRectForOffset = function (clientRect) {
        return {
            bottom: clientRect.bottom + TooltipConsts_1.AnchorClientRectPadding,
            /** 2 * AnchorClientRectPadding accounts for the left and right offsets of AnchorClientRectPadding */
            height: clientRect.height + 2 * TooltipConsts_1.AnchorClientRectPadding,
            left: clientRect.left - TooltipConsts_1.AnchorClientRectPadding,
            right: clientRect.right + TooltipConsts_1.AnchorClientRectPadding,
            top: clientRect.top - TooltipConsts_1.AnchorClientRectPadding,
            /** 2 * AnchorClientRectPadding accounts for the top and bottom offsets of AnchorClientRectPadding */
            width: clientRect.width + 2 * TooltipConsts_1.AnchorClientRectPadding,
        };
    };
    Tooltip.prototype.renderWrappedChildren = function () {
        return (React.createElement(PointerEvents_1.PointerEventWrapper, { pointerClick: this.handleClick },
            React.createElement("div", { onBlur: this.dismissTooltipOverlay, onFocus: this.showTooltipOverlay, onKeyUp: this.handleKeyUp, onMouseEnter: this.showTooltipOverlay, onMouseLeave: this.dismissTooltipOverlay, onMouseMove: this.handleMouseMove, ref: this.handleWrappedChildrenRef, 
                /** Display of 'inline-flex' ensures that the wrapper fits exactly to the contents of the children */
                style: { display: 'inline-flex' }, tabIndex: this.state.containsTabbableChildren ? undefined : 0 }, this.props.children)));
    };
    Tooltip.prototype.renderTooltipOverlay = function () {
        if (!this.state.content || !this.state.anchorClientRect) {
            return undefined;
        }
        var tooltipSideProps = TooltipConsts_1.TooltipPositionInfoMap[this.props.position];
        /**
         * In order to have the content wrapper grow to fit the tooltip contents, we initially render the wrapper with a width of maxWidth.
         * Once the tooltip content is rendered, we get a reference to the content element and get the actual rendered width. This width is
         * then used, along with the left/right padding and border widths to calculate the width of the content wrapper. This results in a
         * content wrapper that fits to the toooltip content.
         */
        var contentWrapperStyle = tslib_1.__assign(tslib_1.__assign({}, TooltipConsts_1.ContentWrapperStyle), { maxWidth: this.props.maxWidth, visibility: this.state.width === undefined ? 'hidden' : 'visible', width: Math.ceil(this.state.width || this.props.maxWidth) });
        return (React.createElement(Overlay_1.Overlay, { allowAutoPosition: true, anchorOrigin: tooltipSideProps.anchorOrigin, disablePointerEvents: true, getAnchorClientRect: this.getAnchorClientRect, shouldAutoPositionOnScrollingRootScroll: this.props.followCursor, stackedOrigin: tooltipSideProps.stackedOrigin, stackingRootId: this.props.stackingRootId, zIndex: this.props.zIndex },
            React.createElement("div", { "data-tb-test-id": this.props.testId, ref: this.handleTooltipOverlayContentRef, role: 'tooltip', style: contentWrapperStyle }, this.state.content)));
    };
    Tooltip.prototype.render = function () {
        return (React.createElement(React.Fragment, null,
            this.renderWrappedChildren(),
            this.renderTooltipOverlay()));
    };
    Tooltip.defaultProps = {
        maxWidth: TooltipConsts_1.DefaultMaxWidth,
        position: TooltipConsts_1.TooltipPosition.Right,
    };
    return Tooltip;
}(React.Component));
exports.Tooltip = Tooltip;
//# sourceMappingURL=Tooltip.js.map